import { parseUploadFormula } from '@erp/shared-utils';
import {
  uploadLayoutColumnProblems,
  watchedUploadColumn,
  type UploadColumn,
  type UploadColumnMapping,
  type UploadLayoutDefinition,
} from '@erp/rpc';

import type { CompiledMapping } from './mapColumn';

/**
 * A layout ready to run: formulas parsed, and every workbook header it reads
 * collected, so a file missing one is refused before any row is mapped.
 */

export interface CompiledColumn {
  column: UploadColumn;
  mapping: CompiledMapping;
}

export interface LayoutProblem {
  column: UploadColumn | null;
  message: string;
}

export interface CompiledLayout {
  columns: CompiledColumn[];
  includeWhenAnyFilled: string[];
  skipWhenZero: string[];
  skipWhenBlank: string[];
  /** Every workbook header the layout reads (the file must have them all). */
  headers: string[];
  /**
   * The headers read for every written row: the filters and the mappings that
   * wait on nothing. A column that waits on another is read only when that
   * column is filled, so its headers are checked only then.
   */
  rowHeaders: string[];
}

export function mappingHeaders(mapping: CompiledMapping): string[] {
  switch (mapping.kind) {
    case 'source':
      return [mapping.header];
    case 'flag':
      return 'header' in mapping.when ? [mapping.when.header] : [];
    case 'formula':
      return mapping.formula.headers;
    default:
      return [];
  }
}

function compileMapping(
  mapping: UploadColumnMapping,
): CompiledMapping | string {
  if (mapping.kind !== 'formula') return mapping;
  const parsed = parseUploadFormula(mapping.expression);
  return parsed.ok ? { ...mapping, formula: parsed.formula } : parsed.error;
}

export function compileLayout(
  definition: UploadLayoutDefinition,
): { layout: CompiledLayout } | { problems: LayoutProblem[] } {
  const problems: LayoutProblem[] = uploadLayoutColumnProblems(
    definition.columns,
  ).map(({ index, message }) => ({
    column: definition.columns[index]?.column ?? null,
    message,
  }));
  const columns: CompiledColumn[] = [];
  for (const { column, mapping } of definition.columns) {
    const compiled = compileMapping(mapping);
    if (typeof compiled === 'string') {
      problems.push({ column, message: compiled });
    } else columns.push({ column, mapping: compiled });
  }
  if (problems.length > 0) return { problems };

  const skipWhenZero = definition.skipWhenZero ?? [];
  const skipWhenBlank = definition.skipWhenBlank ?? [];
  const headers = new Set([
    ...definition.includeWhenAnyFilled,
    ...skipWhenZero,
    ...skipWhenBlank,
  ]);
  const rowHeaders = new Set(headers);
  for (const { mapping } of columns) {
    const waits = watchedUploadColumn(mapping) !== null;
    for (const header of mappingHeaders(mapping)) {
      headers.add(header);
      if (!waits) rowHeaders.add(header);
    }
  }
  return {
    layout: {
      columns,
      includeWhenAnyFilled: definition.includeWhenAnyFilled,
      skipWhenZero,
      skipWhenBlank,
      headers: [...headers],
      rowHeaders: [...rowHeaders],
    },
  };
}
